import classNames from "classnames";
import { useState } from "react";
import { SelectButton } from "../Button/SelectButton.tsx";
import { Icon, type IconName } from "../Icon/Icon.tsx";
import { Popover } from "../Popover/Popover.tsx";
import { DropdownItem } from "./DropdownItem.tsx";

export const SelectionDropdown = <T extends string | number>({
  value,
  onChange,
  options,
  size,
  buttonClassName,
  buttonStyle,
  contentClassName,
  align = "start",
  side = "bottom",
}: {
  value: T;
  onChange: (value: T) => void;
  options: {
    value: T;
    label: string;
    iconName?: IconName;
    extraInfo?: string;
    className?: string;
    style?: React.CSSProperties;
  }[];
  size: "sm" | "md";
  buttonClassName?: string;
  buttonStyle?: React.CSSProperties;
  contentClassName?: string;
  align?: "start" | "end";
  side?: "top" | "bottom";
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find((option) => option.value === value);

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      align={align}
      side={side}
      sideOffset={4}
      className="flex-col max-h-[400px] overflow-y-auto p-200"
      content={
        <div className={classNames("flex-col", contentClassName)}>
          {options.map((option) => (
            <DropdownItem
              key={option.value}
              size={size}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              leftIconName={option.iconName}
              active={option.value === value}
              className={option.className}
              style={option.style}
            >
              <div className="w-full flex-row gap-150 justify-between">
                <div>{option.label}</div>
                {option.extraInfo && (
                  <>
                    &nbsp;
                    <div className="text-disabled">{option.extraInfo}</div>
                  </>
                )}
              </div>
            </DropdownItem>
          ))}
        </div>
      }
    >
      <SelectButton
        size={size}
        className={classNames(
          isOpen && "bg-input-surface-active",
          buttonClassName,
        )}
        isOpen={isOpen}
        style={buttonStyle}
      >
        <div className="flex-row items-center w-full gap-100">
          {selectedOption?.iconName && (
            <Icon name={selectedOption.iconName} size={size} />
          )}
          <div className="flex-fill truncate text-left label-md-default">
            {selectedOption?.label}
          </div>
        </div>
      </SelectButton>
    </Popover>
  );
};
